<template>
  <div v-if="isOpen" class="dialog-overlay" @click="handleCancel()">
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">
        <h2>Edit Training Session</h2>
        <button class="close-btn" @click="handleCancel()">✕</button>
      </div>

      <div class="dialog-body">
        <div class="form-group">
          <label for="session-name">Session Name</label>
          <input
            id="session-name"
            v-model="editedSession.name"
            type="text"
            class="form-input"
            placeholder="Enter session name"
          />
        </div>

        <div class="form-group">
          <label for="session-description">Description</label>
          <textarea
            id="session-description"
            v-model="editedSession.description"
            class="form-textarea"
            placeholder="Enter session description"
            rows="8"
          />
        </div>
      </div>

      <div class="dialog-footer">
        <button class="btn btn-cancel" @click="handleCancel()">
          Cancel
        </button>
        <button
          class="btn btn-save"
          :disabled="!isFormValid"
          @click="handleSave()"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';

const $toast = useToast();

export default {
  name: 'SessionEditDialog',
  props: {
    selectedSessions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['edit-session'],
  data() {
    return {
      isOpen: false,
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      editedSession: {
        id: null,
        name: '',
        description: '',
      },
      originalSession: {},
    };
  },
  computed: {
    isFormValid() {
      return this.editedSession.name && this.editedSession.name.trim().length > 0;
    },
  },
  methods: {
    openModal(session) {
      if (!session) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'Please select a session to edit',
          type: 'warning',
        });
        return;
      }

      this.originalSession = JSON.parse(JSON.stringify(session));
      this.editedSession = {
        id: session.id,
        name: session.name || '',
        description: session.description || '',
      };
      this.isOpen = true;
    },
    closeModal() {
      this.isOpen = false;
    },
    handleCancel() {
      this.closeModal();
    },
    async handleSave() {
      if (!this.isFormValid) {
        return;
      }

      try {
        const response = await this.dataConnect.updateTrainingSession({
          id: this.editedSession.id,
          name: this.editedSession.name,
          description: this.editedSession.description,
        });

        if (response && !response.error) {
          $toast.open({
            queue: true,
            dismissible: true,
            message: 'Session updated successfully',
            type: 'success',
          });
          this.$emit('edit-session', this.editedSession);
          this.closeModal();
        } else {
          throw new Error(response?.error?.message || 'Failed to update session');
        }
      } catch (error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Error: ${error.message}`,
          type: 'error',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog-content {
  background: white;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #000;
  }
}

.dialog-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 14px;
    color: #333;
  }
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb), 0.1);
  }
}

.form-textarea {
  resize: vertical;
  min-height: 200px;
}

.dialog-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-cancel {
  background: #f0f0f0;
  color: #333;
  border: 1px solid #ddd;

  &:hover:not(:disabled) {
    background: #e0e0e0;
  }
}

.btn-save {
  background: var(--color-primary);
  color: white;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
}
</style>
